/**
 * Cliente HTTP mínimo basado en fetch.
 * Inyecta Bearer token desde el auth-store automáticamente.
 */
import { useAuthStore } from "@/lib/auth-store";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";

export class ApiError extends Error {
  status: number;
  details: unknown;

  constructor(message: string, status: number, details: unknown) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  form?: URLSearchParams;
  skipAuth?: boolean;
}

export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { body, form, skipAuth, headers, ...rest } = opts;
  const h = new Headers(headers);

  let payload: BodyInit | undefined;
  if (form) {
    h.set("content-type", "application/x-www-form-urlencoded");
    payload = form;
  } else if (body !== undefined) {
    h.set("content-type", "application/json");
    payload = JSON.stringify(body);
  }

  if (!skipAuth) {
    const token = useAuthStore.getState().accessToken;
    if (token) h.set("authorization", `Bearer ${token}`);
  }

  const init: RequestInit = { ...rest, headers: h };
  if (payload !== undefined) init.body = payload;
  const res = await fetch(`${API_BASE_URL}${path}`, init);

  const contentType = res.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const data = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    const detail =
      typeof data === "object" && data !== null && "detail" in data
        ? String((data as Record<string, unknown>).detail)
        : `HTTP ${res.status}`;
    throw new ApiError(detail, res.status, data);
  }

  return data as T;
}
